/**
 * Game API service layer.
 *
 * Each function accepts only the minimal data needed (character IDs, not
 * full objects) and delegates to apiPost(). The Wortou browser SDK
 * auto-attaches user tokens and handles 401/402 with login/payment popups.
 */
import { apiPost } from './client';
import type { ChatMessage, GameCard, ActiveBuff } from '../../types';
import type {
  GenerateChatRequest,
  GenerateChatResponse,
  GenerateCardsRequest,
  GenerateCardsResponse,
  GenerateEndingRequest,
  GenerateEndingResponse,
} from '@shared/types/api';

/**
 * Generates chat response bubbles from one character to another.
 * Returns dialogue texts, a CP change value, and landmine trigger info.
 */
export async function generateChatResponse(
  speakerId: string,
  listenerId: string,
  history: ChatMessage[],
  injectedInstruction?: string,
  cpValueSpeaker?: number,
  cpValueListener?: number,
  pastEvents?: string[],
  speakerBuffs?: ActiveBuff[],
): Promise<{ texts: string[]; cpChange: number; landmineTriggered: string | null }> {
  const body: GenerateChatRequest = {
    speakerId, listenerId, history, injectedInstruction,
    cpValueSpeaker, cpValueListener, pastEvents,
    speakerBuffs: speakerBuffs?.length ? speakerBuffs : undefined,
  };
  const res = await apiPost<GenerateChatResponse>('/api/generate-chat', body);
  return { texts: res.texts, cpChange: res.cpChange, landmineTriggered: res.landmineTriggered };
}

/**
 * Generates 9 intervention cards based on current context.
 */
export async function generateCards(
  charAId: string,
  charBId: string,
  history: ChatMessage[],
  cpA: number,
  cpB: number,
): Promise<GameCard[]> {
  const body: GenerateCardsRequest = { charAId, charBId, history, cpA, cpB };
  const res = await apiPost<GenerateCardsResponse>('/api/generate-cards', body);
  return res.cards as unknown as GameCard[];
}

/**
 * Generates the final ending narrative.
 */
export async function generateEnding(
  charAId: string,
  charBId: string,
  history: ChatMessage[],
  finalCpA: number,
  finalCpB: number,
): Promise<GenerateEndingResponse> {
  const body: GenerateEndingRequest = { charAId, charBId, history, finalCpA, finalCpB };
  return apiPost<GenerateEndingResponse>('/api/generate-ending', body);
}
